import { useQuery } from '@apollo/client';
import Box from '@risk-smart/themed-cloudscape-components/box';
import ErrorContent from '@risksmart-app/components/src/error-pages/ErrorContent';
import { getEnv } from '@risksmart-app/components/src/utils/environment';
import { isE2eTestOrg } from '@risksmart-app/feature-flags/src/e2e-test-org';
import { parseIgnoreE2eOrgOverride } from '@risksmart-app/feature-flags/src/ignore-e2e-org-override';
import { parseOrgFeatures } from '@risksmart-app/modules/src/index';
import type { Meta } from '@risksmart-app/shared/organisation/Meta';
import { GetOrganisationDocument } from '@risksmart-app/web-graphql-client/generated/graphql';
import { type FC, type ReactNode, useMemo } from 'react';
import Loading from 'src/components/loading';

import { E2eFlagOverridesContext } from './E2eFlagOverridesContext';
import { FeaturesContext } from './FeaturesContext';

type Props = { children: ReactNode };

export const FeaturesProvider: FC<Props> = ({ children }) => {
  const { data, loading, error } = useQuery(GetOrganisationDocument);
  const organisation = data?.auth_organisation;
  const meta: Meta | null | undefined = organisation?.[0]?.Meta;
  const features = useMemo(
    () => (meta?.features ? parseOrgFeatures(meta.features) : []),
    [meta?.features]
  );
  const overrideIgnored = parseIgnoreE2eOrgOverride(
    getEnv('REACT_APP_FEATURE_FLAG_IGNORE_E2E_ORG_OVERRIDE', true)
  );
  // Shared predicate, not `=== true`: the JSONB column holds the string
  // `"true"` on every seeded e2e org, so strict equality silently disabled this
  // override and seeded flags were ignored (ENG-619). ENG-645 forces `null`.
  const e2eFlagOverrides =
    isE2eTestOrg(meta) && !overrideIgnored ? features : null;

  return (
    <FeaturesContext.Provider value={features}>
      <E2eFlagOverridesContext.Provider value={e2eFlagOverrides}>
        {loading && <Loading data-loading-reason={'features'} />}
        {error && (
          <ErrorContent
            title={'Sorry, we couldn’t make that happen'}
            imgSrc={'/errors/rubiks-cube.png'}
            imgAlt={'binoculars'}
          >
            <Box variant={'p'}>{'Please try again later'}</Box>
          </ErrorContent>
        )}
        {!error && !loading && children}
      </E2eFlagOverridesContext.Provider>
    </FeaturesContext.Provider>
  );
};
